import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Clock,
    Calendar,
    Eye,
    ArrowLeft,
    Share2,
    Check,
    CheckCircle,
    XCircle,
    Edit3,
    Trash2,
    AlertTriangle,
    Heart,
    Bookmark,
    MessageCircle,
    Send,
    Loader2
} from "lucide-react";
import { useAuth, useMessage } from "../../global-components/src";
import { api } from "../../services/api";
import { BlogPost, BlogComment } from "../../global-components/src/types";
import { getImageUser, getImageUserNotFound } from "../../global-components/src/utils/auth";
import { BUCKET_PACOCA_URL } from "../../global-components/src/utils/vars";
import ImgVerified from "../../global-components/src/components/img-verified";
import BlogNavbar from "./components/BlogNavbar";
import "./blog-article.css";

const coverSrc = (v: string) => v.startsWith("http") ? v : `${BUCKET_PACOCA_URL}/${v}`;

const BlogArticle: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { slug } = useParams<{ slug: string }>();
    const { token, user } = useAuth();
    const { setMessage } = useMessage();
    const navigate = useNavigate();

    const [post, setPost] = useState<BlogPost | null>(null);
    const [canModerate, setCanModerate] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    // Interactions state
    const [liked, setLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(0);
    const [likeLoading, setLikeLoading] = useState(false);

    const [saved, setSaved] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);

    const [comments, setComments] = useState<BlogComment[]>([]);
    const [commentsCount, setCommentsCount] = useState(0);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [showComments, setShowComments] = useState(true);
    const [commentText, setCommentText] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

    const commentInputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (slug) {
            fetchArticle(slug);
        }
    }, [slug, token]);

    useEffect(() => {
        if (post && comments.length === 0) {
            fetchComments();
        }
    }, [post?.id]);

    const fetchArticle = async (articleSlug: string) => {
        try {
            setLoading(true);
            const res = await api(token).get(`/blog/posts/${articleSlug}`);
            const p: BlogPost = res.data?.post || null;
            setPost(p);
            setCanModerate(Boolean(res.data?.can_moderate));
            setCanEdit(Boolean(res.data?.can_edit));
            if (p) {
                setLiked(p.liked ?? false);
                setLikesCount(p.likes_count ?? 0);
                setSaved(p.saved ?? false);
                setCommentsCount(p.comments_count ?? 0);
            }
        } catch (err: any) {
            setMessage(t("blog.notFound"), "error");
            navigate("/blog");
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!post) return;
        try {
            setActionLoading(true);
            await api(token).post(`/blog/${post.id}/approve`);
            setMessage(t("blog.approveSuccess"), "success");
            fetchArticle(post.slug);
        } catch (err: any) {
            setMessage(t("blog.approveError"), "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!post) return;
        const reason = window.prompt(t("blog.rejectReasonPrompt"), t("blog.defaultRejectReason"));
        if (reason === null) return;

        try {
            setActionLoading(true);
            await api(token).post(`/blog/${post.id}/reject`, { reason });
            setMessage(t("blog.rejectSuccess"), "warning");
            fetchArticle(post.slug);
        } catch (err: any) {
            setMessage(t("blog.rejectError"), "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!post) return;
        if (!window.confirm(t("blog.confirmDelete"))) return;

        try {
            setActionLoading(true);
            await api(token).delete(`/blog/${post.id}`);
            setMessage(t("blog.deleteSuccess"), "success");
            navigate("/blog");
        } catch (err: any) {
            setMessage(t("blog.deleteError"), "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setMessage(t("blog.linkCopiedToast"), "success");
        setTimeout(() => setCopied(false), 2500);
    };

    // ---- Like ----
    const handleLike = async () => {
        if (!post || !token) {
            navigate("/login");
            return;
        }
        if (likeLoading) return;
        const wasLiked = liked;
        setLiked(!wasLiked);
        setLikesCount((c) => c + (wasLiked ? -1 : 1));
        setLikeLoading(true);
        try {
            await api(token).post(`/blog/${post.id}/like`);
        } catch (err: any) {
            // revert on error
            setLiked(wasLiked);
            setLikesCount((c) => c + (wasLiked ? 1 : -1));
            setMessage(t("blog.likeError"), "error");
        } finally {
            setLikeLoading(false);
        }
    };

    // ---- Save ----
    const handleSave = async () => {
        if (!post || !token) {
            navigate("/login");
            return;
        }
        if (saveLoading) return;
        const wasSaved = saved;
        setSaved(!wasSaved);
        setSaveLoading(true);
        try {
            await api(token).post(`/blog/${post.id}/save`);
            setMessage(!wasSaved ? t("blog.savedSuccess") : t("blog.unsavedSuccess"), "success");
        } catch (err: any) {
            setSaved(wasSaved);
            setMessage(t("blog.saveError"), "error");
        } finally {
            setSaveLoading(false);
        }
    };

    // ---- Comments ----
    const fetchComments = async () => {
        if (!post) return;
        setCommentsLoading(true);
        try {
            const res = await api(token).get(`/blog/${post.id}/comments`);
            setComments(res.data?.comments || []);
        } catch (err: any) {
            setMessage(t("blog.commentsLoadError"), "error");
        } finally {
            setCommentsLoading(false);
        }
    };

    const toggleComments = () => {
        if (!showComments) {
            setShowComments(true);
            if (comments.length === 0) {
                fetchComments();
            }
        } else {
            setShowComments(false);
        }
    };

    const handleSubmitComment = async () => {
        if (!post || !token) {
            navigate("/login");
            return;
        }
        const text = commentText.trim();
        if (!text || text.length < 2) {
            setMessage(t("blog.commentTooShort"), "error");
            return;
        }
        setSubmittingComment(true);
        try {
            const res = await api(token).post(`/blog/${post.id}/comments`, { content: text });
            const newComment: BlogComment = res.data?.comment;
            if (newComment) {
                setComments((prev) => [newComment, ...prev]);
                setCommentsCount((c) => c + 1);
            }
            setCommentText("");
        } catch (err: any) {
            setMessage(t("blog.commentError"), "error");
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId: number) => {
        if (!post) return;
        setDeletingCommentId(commentId);
        try {
            await api(token).delete(`/blog/${post.id}/comments/${commentId}`);
            setComments((prev) => prev.filter((c) => c.id !== commentId));
            setCommentsCount((c) => Math.max(0, c - 1));
        } catch (err: any) {
            setMessage(t("blog.commentDeleteError"), "error");
        } finally {
            setDeletingCommentId(null);
        }
    };

    if (loading) {
        return (
            <div className="blog-article-loading-page">
                <BlogNavbar />
                <div className="blog-article-loading">
                    <p>{t("generics.loading")}</p>
                </div>
            </div>
        );
    }

    if (!post) {
        return null;
    }

    const dateSource = post.published_at ? new Date(post.published_at) : new Date(post.created_at);
    const formattedDate = dateSource.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "long",
        year: "numeric"
    });

    const pageUrl = window.location.href;

    return (
        <div className="blog-article-page">
            <Helmet>
                <title>{post.title} – Blog do Paçoca</title>
                <meta name="description" content={post.summary || post.title} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={`${post.title} – Blog do Paçoca`} />
                <meta property="og:description" content={post.summary || post.title} />
                {post.cover_image && <meta property="og:image" content={coverSrc(post.cover_image)} />}
                <meta property="og:url" content={pageUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={post.title} />
                <meta name="twitter:description" content={post.summary || post.title} />
                {post.cover_image && <meta name="twitter:image" content={coverSrc(post.cover_image)} />}
            </Helmet>

            <BlogNavbar />

            {/* Moderation / Non-published status banner */}
            {post.status !== "published" && (
                <div className={`blog-moderation-sticky-bar status-${post.status}`}>
                    <div className="blog-mod-bar-inner">
                        <div className="blog-mod-bar-info">
                            <AlertTriangle size={18} />
                            <span>
                                {post.status === "pending" && t("blog.sticky.pending")}
                                {post.status === "draft" && t("blog.sticky.draft")}
                                {post.status === "rejected" && t("blog.sticky.rejected", { reason: post.rejection_reason || "" })}
                            </span>
                        </div>

                        {canModerate && post.status === "pending" && (
                            <div className="blog-mod-bar-actions">
                                <button
                                    type="button"
                                    onClick={handleApprove}
                                    disabled={actionLoading}
                                    className="blog-mod-btn-approve"
                                >
                                    <CheckCircle size={15} />
                                    <span>{t("blog.approve")}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={handleReject}
                                    disabled={actionLoading}
                                    className="blog-mod-btn-reject"
                                >
                                    <XCircle size={15} />
                                    <span>{t("blog.reject")}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <main className="blog-article-main">
                <div className="blog-article-container">
                    <div className="blog-article-top-nav">
                        <Link to="/blog" className="blog-back-link">
                            <ArrowLeft size={16} />
                            <span>{t("blog.backToBlog")}</span>
                        </Link>

                        <div className="blog-article-top-actions">
                            {canEdit && (
                                <button
                                    type="button"
                                    onClick={() => navigate(`/blog/editar/${post.id}`)}
                                    className="blog-action-icon-btn"
                                    title={t("blog.editArticle")}
                                >
                                    <Edit3 size={16} />
                                </button>
                            )}
                            {canEdit && (
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className="blog-action-icon-btn text-danger"
                                    title={t("blog.deleteArticle")}
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={handleCopyLink}
                                className="blog-action-icon-btn"
                                title={t("blog.shareLink")}
                            >
                                {copied ? <Check size={16} className="text-success" /> : <Share2 size={16} />}
                            </button>
                        </div>
                    </div>

                    <header className="blog-article-header">
                        <h1 className="blog-article-title">{post.title}</h1>

                        {post.summary && <p className="blog-article-lead">{post.summary}</p>}

                        {/* Author info */}
                        <div className="blog-article-author-card">
                            {post.user ? (
                                <Link to={`/@${post.user.user_name}`} className="blog-author-avatar-link">
                                    <img
                                        src={getImageUser(post.user.img_account, undefined, post.user.name)}
                                        onError={(e) => { e.currentTarget.src = getImageUserNotFound(post.user?.name); }}
                                        alt={post.user.name}
                                        className="blog-author-avatar"
                                    />
                                </Link>
                            ) : null}

                            <div className="blog-author-meta">
                                {post.user ? (
                                    <Link to={`/@${post.user.user_name}`} className="blog-author-name">
                                        <span>{post.user.name}</span>
                                        <ImgVerified user={post.user} classNameProp="blog-verified-badge" />
                                    </Link>
                                ) : null}

                                <div className="blog-article-stats">
                                    <span className="blog-article-stat-item">
                                        <Calendar size={13} />
                                        {formattedDate}
                                    </span>
                                    <span className="blog-stat-dot">•</span>
                                    <span className="blog-article-stat-item">
                                        <Clock size={13} />
                                        {t("blog.readTime", { count: post.read_time })}
                                    </span>
                                    {post.views_count > 0 && (
                                        <>
                                            <span className="blog-stat-dot">•</span>
                                            <span className="blog-article-stat-item">
                                                <Eye size={13} />
                                                {t("blog.viewsCount", { count: post.views_count })}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </header>

                    {/* Cover image */}
                    {post.cover_image && (
                        <div className="blog-article-cover-wrapper">
                            <img src={coverSrc(post.cover_image!)} alt={post.title} className="blog-article-cover-img" />
                        </div>
                    )}

                    {/* Article Body Content — HTML from CKEditor */}
                    <article
                        className="blog-article-content blog-ck-rendered"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />

                    {/* ── Interaction Bar ── */}
                    <div className="blog-interaction-bar">
                        {/* Like */}
                        <button
                            type="button"
                            className={`blog-interact-btn ${liked ? "liked" : ""}`}
                            onClick={handleLike}
                            disabled={likeLoading}
                            title={t("blog.like")}
                        >
                            <Heart size={18} className={liked ? "filled" : ""} />
                            <span>{likesCount > 0 ? likesCount : t("blog.like")}</span>
                        </button>

                        {/* Comments toggle */}
                        <button
                            type="button"
                            className={`blog-interact-btn ${showComments ? "active" : ""}`}
                            onClick={toggleComments}
                            title={t("blog.comments")}
                        >
                            <MessageCircle size={18} />
                            <span>{commentsCount > 0 ? commentsCount : t("blog.comments")}</span>
                        </button>

                        {/* Save */}
                        <button
                            type="button"
                            className={`blog-interact-btn ${saved ? "saved" : ""}`}
                            onClick={handleSave}
                            disabled={saveLoading}
                            title={saved ? t("blog.unsave") : t("blog.save")}
                        >
                            <Bookmark size={18} className={saved ? "filled" : ""} />
                            <span>{saved ? t("blog.saved") : t("blog.save")}</span>
                        </button>
                    </div>

                    {/* ── Comments Section ── */}
                    {showComments && (
                        <div className="blog-comments-section">
                            {/* Comment Input */}
                            {token ? (
                                <div className="blog-comment-input-row">
                                    {user && (
                                        <img
                                            src={getImageUser(user.img_account, undefined, user.name)}
                                            onError={(e) => { e.currentTarget.src = getImageUserNotFound(user.name); }}
                                            alt={user.name}
                                            className="blog-comment-avatar"
                                        />
                                    )}
                                    <div className="blog-comment-input-wrap">
                                        <textarea
                                            ref={commentInputRef}
                                            value={commentText}
                                            onChange={(e) => setCommentText(e.target.value)}
                                            placeholder={t("blog.commentPlaceholder")}
                                            className="blog-comment-textarea"
                                            rows={2}
                                            maxLength={1000}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                                    handleSubmitComment();
                                                }
                                            }}
                                        />
                                        <div className="blog-comment-input-footer">
                                            <span className="blog-comment-char">{commentText.length} / 1000</span>
                                            <button
                                                type="button"
                                                className="blog-comment-submit-btn"
                                                onClick={handleSubmitComment}
                                                disabled={submittingComment || commentText.trim().length < 2}
                                            >
                                                {submittingComment ? (
                                                    <Loader2 size={15} className="blog-btn-spin" />
                                                ) : (
                                                    <Send size={15} />
                                                )}
                                                <span>{t("blog.submitComment")}</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="blog-comments-login-prompt">
                                    <Link to="/login">{t("blog.loginToComment")}</Link>
                                </p>
                            )}

                            {/* Comments List */}
                            {commentsLoading ? (
                                <div className="blog-comments-loading">
                                    <Loader2 size={20} className="blog-btn-spin" />
                                </div>
                            ) : comments.length === 0 ? (
                                <p className="blog-comments-empty">{t("blog.noComments")}</p>
                            ) : (
                                <div className="blog-comments-list">
                                    {comments.map((comment) => (
                                        <div key={comment.id} className="blog-comment-item">
                                            {comment.user ? (
                                                <Link to={`/@${comment.user.user_name}`} className="blog-comment-avatar-link">
                                                    <img
                                                        src={getImageUser(comment.user.img_account, undefined, comment.user.name)}
                                                        onError={(e) => { e.currentTarget.src = getImageUserNotFound(comment.user?.name); }}
                                                        alt={comment.user.name}
                                                        className="blog-comment-avatar"
                                                    />
                                                </Link>
                                            ) : null}
                                            <div className="blog-comment-body">
                                                <div className="blog-comment-header">
                                                    {comment.user && (
                                                        <Link to={`/@${comment.user.user_name}`} className="blog-comment-author">
                                                            {comment.user.name}
                                                        </Link>
                                                    )}
                                                    <span className="blog-comment-date">
                                                        {new Date(comment.created_at).toLocaleDateString("pt-BR", {
                                                            day: "numeric",
                                                            month: "short",
                                                            year: "numeric"
                                                        })}
                                                    </span>
                                                    {(user?.id === comment.user_id || canModerate) && (
                                                        <button
                                                            type="button"
                                                            className="blog-comment-delete-btn"
                                                            onClick={() => handleDeleteComment(comment.id)}
                                                            disabled={deletingCommentId === comment.id}
                                                            title={t("blog.deleteComment")}
                                                        >
                                                            {deletingCommentId === comment.id ? (
                                                                <Loader2 size={13} className="blog-btn-spin" />
                                                            ) : (
                                                                <Trash2 size={13} />
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="blog-comment-text">{comment.content}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Footer / Share */}
                    <footer className="blog-article-footer">
                        <div className="blog-footer-share-box">
                            <span className="blog-share-title">{t("blog.sharePrompt")}</span>
                            <div className="blog-share-buttons">
                                <button onClick={handleCopyLink} className="blog-share-btn" type="button">
                                    {copied ? <Check size={14} /> : <Share2 size={14} />}
                                    <span>{copied ? t("blog.linkCopied") : t("blog.copyLink")}</span>
                                </button>
                            </div>
                        </div>
                    </footer>
                </div>
            </main>
        </div>
    );
};

export default BlogArticle;
